import React from 'react';
import { ShortCode } from '@highereducation/wordpress';
import { NavTabs, type NavTabItem } from '@highereducation/hen-ui';
import type { LucideIconName } from '@highereducation/hen-ui';

type NavTabsItemShortcodeProps = {
  value?: string;
  label?: string;
  href?: string;
  icon?: string;
  disabled?: string;
  [key: string]: unknown;
};

type NavTabsShortcodeProps = {
  'active-value'?: string;
  variant?: 'card' | 'underline';
  children?: React.ReactNode;
  [key: string]: unknown;
};

/**
 * NavTabsShortcode - Shortcode wrapper for navigation tabs.
 *
 * Hash `href`s (`#section-id`) pick up active state from the URL automatically.
 * Full URL tabs should set `active-value`.
 *
 * @example Parent hub (in-page anchors)
 * [hen-ui-nav-tabs variant="underline"]
 *   [hen-ui-nav-tabs-item value="all" label="All" href="#category-hub-top"]
 *   [hen-ui-nav-tabs-item value="addiction-work" label="Addiction Work" href="#addiction-work"]
 * [/hen-ui-nav-tabs]
 *
 * @example Route nav (cross-page links)
 * [hen-ui-nav-tabs variant="underline" active-value="all"]
 *   [hen-ui-nav-tabs-item value="all" label="All" href="/careers/"]
 *   [hen-ui-nav-tabs-item value="addiction-work" label="Addiction Work" href="/addiction-work/"]
 * [/hen-ui-nav-tabs]
 */
export const NavTabsShortcode: ShortCode<NavTabsShortcodeProps> = {
  component: ({
    'active-value': activeValue = '',
    variant = 'card',
    children,
  }) => {
    const tabs: NavTabItem[] = React.Children.toArray(children)
      .filter((child): child is React.ReactElement<NavTabsItemShortcodeProps> =>
        React.isValidElement(child),
      )
      .map((child) => ({
        value: child.props.value ?? '',
        label: child.props.label ?? '',
        href: child.props.href ?? '#',
        ...(child.props.icon && { icon: child.props.icon as LucideIconName }),
        disabled: child.props.disabled === 'true',
      }))
      .filter((tab) => !tab.disabled);

    if (tabs.length === 0) return null;

    return (
      <NavTabs
        tabs={tabs}
        activeValue={activeValue || undefined}
        variant={variant === 'underline' ? 'underline' : 'default'}
      />
    );
  },
};

export const NavTabsItemShortcode: ShortCode<NavTabsItemShortcodeProps> = {
  component: () => null,
};
